import { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { latLonToVec3 } from './latLonToVec3';

const labelStyle = (color) => ({
  fontFamily: 'Satoshi, system-ui, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#0A0D10',
  background: color,
  padding: '4px 9px',
  borderRadius: 5,
  whiteSpace: 'nowrap',
  boxShadow: `0 4px 20px ${color}88`,
  transform: 'translateY(-6px)',
});

// Pin che spunta dalla superficie in lat/lon; si rimpicciolisce avvicinandosi al suolo.
export default function LandPin({ lat, lon, radius = 1, label, color = '#b4ff00', onClick, pinned = false, labelPortal }) {
  const groupRef = useRef();
  const beamRef = useRef();
  const haloRef = useRef();
  const [hovered, setHovered] = useState(false);
  const showLabel = pinned || hovered;

  const { pos, quat } = useMemo(() => {
    const base = latLonToVec3(lat, lon, radius);
    return {
      pos: base.clone().multiplyScalar(1.001),
      quat: new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), base.clone().normalize()),
    };
  }, [lat, lon, radius]);

  useFrame(({ camera }) => {
    const t = performance.now() * 0.0025;
    if (beamRef.current) beamRef.current.scale.y = 1 + Math.sin(t) * 0.15;
    if (haloRef.current) haloRef.current.scale.setScalar(1 + Math.sin(t * 1.3) * 0.25);
    if (groupRef.current) {
      const h = camera.position.length() - 1;
      groupRef.current.scale.setScalar(Math.min(1, Math.max(0.05, h / 2.3)));
    }
  });

  const height = 0.055;

  return (
    <group
      ref={groupRef}
      position={pos}
      quaternion={quat}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; }}
      onPointerOut={() => { setHovered(false); document.body.style.cursor = ''; }}
      onClick={(e) => { if (!onClick) return; e.stopPropagation(); onClick(e); }}
    >
      <mesh position={[0, 0.001, 0]}>
        <cylinderGeometry args={[0.014, 0.014, 0.003, 20]} />
        <meshBasicMaterial color={color} toneMapped={false} transparent opacity={0.9} />
      </mesh>
      <mesh ref={beamRef} position={[0, height / 2, 0]}>
        <cylinderGeometry args={[0.003, 0.006, height, 10]} />
        <meshBasicMaterial color={color} toneMapped={false} />
      </mesh>
      <mesh position={[0, height + 0.012, 0]}>
        <sphereGeometry args={[0.014, 16, 16]} />
        <meshBasicMaterial color={color} toneMapped={false} />
      </mesh>
      <mesh ref={haloRef} position={[0, height + 0.012, 0]}>
        <sphereGeometry args={[0.03, 16, 16]} />
        <meshBasicMaterial color={color} toneMapped={false} transparent opacity={0.22} depthWrite={false} />
      </mesh>
      {label && showLabel && (
        <Html position={[0, height + 0.04, 0]} center portal={labelPortal} style={{ pointerEvents: 'none' }}>
          <div style={labelStyle(color)}>{label}</div>
        </Html>
      )}
    </group>
  );
}
